import { inject } from '@angular/core';
import { TranslationService, LanguageConfigService, AppInsightsService, UserInfoService } from '@flexbenefits-ui/services';
import { ENVIRONMENT } from '@flexbenefits-ui/data-access-api';
import { firstValueFrom } from 'rxjs';

/**
 * App Initializer Factory
 * 
 * Factory function to initialize all services before the application starts.
 * Currently handles:
 * - Application Insights initialization
 * - Translation service initialization
 * - User info service initialization (fetches user language preference)
 * - Language configuration from external service
 * 
 * Priority order for language:
 * 1. Language from user info API (locale field)
 * 2. Language from external service API
 * 3. Language from localStorage (user preference)
 * 4. Default language (fallback)
 * 
 * @returns A function that returns a Promise for APP_INITIALIZER
 */
export function appInitializerFactory(): () => Promise<void> {
    return async () => {
        const DEFAULT_LOCALE = 'nl';
        const appInsightsService = inject(AppInsightsService);
        const translationService = inject(TranslationService);
        const languageConfigService = inject(LanguageConfigService);
        const userInfoService = inject(UserInfoService);
        const environment = inject(ENVIRONMENT);
        const translateService = translationService.getTranslateService();

        // Initialize Application Insights
        appInsightsService.initialize(environment.appInsights?.instrumentationKey);

        try {
            // Determine initial locale with priority: user info > external service > localStorage > default
            const storedLanguage = languageConfigService.getStoredLanguage();
            let initialLocale = DEFAULT_LOCALE;

            // Priority 1: Try to get language from user info API
            try {
                const userLanguage = await firstValueFrom(userInfoService.getUserLanguage());
                
                if (translationService.isLocaleAvailable(userLanguage)) {
                    initialLocale = userLanguage;
                    console.log(`[App Initializer] Using language from user info: ${userLanguage}`);
                } else {
                    throw new Error('User language not available');
                }
            } catch (userInfoError) {
                console.warn('[App Initializer] Failed to fetch user language, trying external service', userInfoError);
                
                // Priority 2: Try external service
                try {
                    const externalLanguage = await firstValueFrom(
                        languageConfigService.fetchLanguageFromExternalService()
                    );
                    initialLocale = translationService.isLocaleAvailable(externalLanguage)
                        ? externalLanguage
                        : storedLanguage && translationService.isLocaleAvailable(storedLanguage)
                            ? storedLanguage
                            : DEFAULT_LOCALE;
                } catch {
                    // Priority 3: External service unavailable, fallback to stored or default
                    initialLocale = storedLanguage && translationService.isLocaleAvailable(storedLanguage)
                        ? storedLanguage
                        : DEFAULT_LOCALE;
                }
            }

            // Initialize translation service
            translateService.setDefaultLang(DEFAULT_LOCALE);
            await firstValueFrom(translateService.use(initialLocale));
            languageConfigService.saveLanguage(initialLocale);
            
            console.log(`[App Initializer] Initialized with locale: ${initialLocale}`);
        } catch (error) {
            console.error('[App Initializer] Failed to initialize', error);
            translateService.setDefaultLang(DEFAULT_LOCALE);
            await firstValueFrom(translateService.use(DEFAULT_LOCALE));
        }
    };
}
